import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip, Button, Select, MenuItem, FormControl, InputLabel, TablePagination, Checkbox,
  TableSortLabel, Snackbar, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions,
  DialogContentText, TextField, Tooltip, Alert
} from '@mui/material';
import { Visibility, Download, Assessment, Block, RestoreFromTrash } from '@mui/icons-material';
import { applicationsAPI } from '../../services/api';
import dayjs from 'dayjs';

const STATUS_COLORS = {
  submitted: 'default', under_review: 'info', reviewed: 'primary',
  accepted: 'success', rejected: 'error', waitlisted: 'warning',
};
const STATUSES = Object.keys(STATUS_COLORS);

export default function ApplicationList() {
  const { id: formId } = useParams();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState([]);
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('desc');
  const [bulkStatus, setBulkStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  // view: 'live' = in the review pool, 'discarded' = kept for the record only
  const [view, setView] = useState('live');
  const [discardTargets, setDiscardTargets] = useState(null);
  const [discardReason, setDiscardReason] = useState('');
  const [discardError, setDiscardError] = useState('');

  const load = () => {
    applicationsAPI.list(formId, { page: page + 1, per_page: perPage, status: status || undefined, view: view })
      .then((r) => { setApps(r.data.data); setTotal(r.data.total); setSelected([]); });
  };
  useEffect(load, [formId, page, perPage, status, view]);

  const handleExport = async () => {
    const res = await applicationsAPI.export(formId, view);
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a'); a.href = url; a.download = 'applications.csv'; a.click();
  };

  const open = (id) => navigate(`/admin/forms/${formId}/applications/${id}`);

  const sortedApps = (function () {
    if (!sortBy) return apps;
    const copy = apps.slice();
    copy.sort(function (a, b) {
      let va, vb;
      if (sortBy === 'avg_score') { va = a.avg_score == null ? -1 : a.avg_score; vb = b.avg_score == null ? -1 : b.avg_score; }
      else if (sortBy === 'review_count') { va = a.review_count || 0; vb = b.review_count || 0; }
      else { va = a.submitted_at; vb = b.submitted_at; }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  })();

  const requestSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : prev.concat([id]));
  };
  const toggleSelectAll = () => {
    if (selected.length === sortedApps.length) setSelected([]);
    else setSelected(sortedApps.map((a) => a.id));
  };

  const changeStatus = async (id, newStatus) => {
    setBusy(true);
    try { await applicationsAPI.update(id, { status: newStatus }); setToast('Status updated'); load(); }
    catch (e) { setToast('Update failed'); }
    setBusy(false);
  };

  const applyBulk = async () => {
    if (!bulkStatus || selected.length === 0) return;
    setBusy(true);
    try {
      for (let i = 0; i < selected.length; i++) { await applicationsAPI.update(selected[i], { status: bulkStatus }); }
      setToast(`${selected.length} updated`); setBulkStatus(''); load();
    } catch (e) { setToast('Bulk update failed'); }
    setBusy(false);
  };

  const openDiscard = (ids) => { setDiscardTargets(ids); setDiscardReason(''); setDiscardError(''); };

  const confirmDiscard = async () => {
    setBusy(true);
    const failures = [];
    for (let i = 0; i < discardTargets.length; i++) {
      try {
        await applicationsAPI.discard(discardTargets[i], discardReason);
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.error) || 'could not be discarded';
        const app = apps.filter((a) => a.id === discardTargets[i])[0];
        failures.push(`${app ? app.applicant_name : 'One application'}: ${msg}`);
      }
    }
    setBusy(false);
    if (failures.length) { setDiscardError(failures.join(' \u2014 ')); load(); return; }
    setDiscardTargets(null);
    setToast(discardTargets.length === 1 ? 'Application discarded' : `${discardTargets.length} applications discarded`);
    load();
  };

  const restore = async (id) => {
    setBusy(true);
    try { await applicationsAPI.restore(id); setToast('Application restored'); load(); }
    catch (e) { setToast('Restore failed'); }
    setBusy(false);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4">Applications</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select value={status} label="Status" onChange={(e) => { setStatus(e.target.value); setPage(0); }}>
              <MenuItem value="">All</MenuItem>
              {STATUSES.map((s) => <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>)}
            </Select>
          </FormControl>
          <Button startIcon={<Assessment />} onClick={() => navigate(`/admin/forms/${formId}/criteria`)}>Criteria</Button>
          <Button startIcon={<Download />} variant="outlined" onClick={handleExport}>Export CSV</Button>
        </Box>
      </Box>

      <Tabs value={view} onChange={(_, v) => { setView(v); setPage(0); }}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="live" label="In review" />
        <Tab value="discarded" label="Discarded" />
      </Tabs>

      {view === 'discarded' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          These applications are out of the review pool. Nothing has been deleted &mdash; their
          answers, files and any reviews are still stored, and restoring puts them back.
        </Alert>
      )}

      {selected.length > 0 && (
        <Card sx={{ mb: 2, p: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', bgcolor: 'action.hover' }}>
          <Typography variant="body2">{selected.length} selected</Typography>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Set status to</InputLabel>
            <Select value={bulkStatus} label="Set status to" onChange={(e) => setBulkStatus(e.target.value)}>
              {STATUSES.map((s) => <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="contained" disabled={!bulkStatus || busy} onClick={applyBulk}>Apply to {selected.length}</Button>
          {view === 'live' && (
            <Button color="warning" startIcon={<Block />} disabled={busy}
              onClick={() => openDiscard(selected)}>Discard {selected.length}</Button>
          )}
          <Button onClick={() => setSelected([])}>Clear</Button>
        </Card>
      )}

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < sortedApps.length}
                    checked={sortedApps.length > 0 && selected.length === sortedApps.length}
                    onChange={toggleSelectAll} />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell sortDirection={sortBy === 'review_count' ? sortDir : false}>
                  <TableSortLabel active={sortBy === 'review_count'} direction={sortBy === 'review_count' ? sortDir : 'desc'}
                    onClick={() => requestSort('review_count')}>Reviews</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'avg_score' ? sortDir : false}>
                  <TableSortLabel active={sortBy === 'avg_score'} direction={sortBy === 'avg_score' ? sortDir : 'desc'}
                    onClick={() => requestSort('avg_score')}>Avg Score</TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'submitted_at' ? sortDir : false}>
                  <TableSortLabel active={sortBy === 'submitted_at'} direction={sortBy === 'submitted_at' ? sortDir : 'desc'}
                    onClick={() => requestSort('submitted_at')}>Submitted</TableSortLabel>
                </TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedApps.map((a) => (
                <TableRow key={a.id} hover selected={selected.includes(a.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox checked={selected.includes(a.id)} onChange={() => toggleSelect(a.id)} />
                  </TableCell>
                  <TableCell sx={{ cursor: 'pointer' }} onClick={() => open(a.id)}>{a.applicant_name}</TableCell>
                  <TableCell sx={{ cursor: 'pointer' }} onClick={() => open(a.id)}>{a.applicant_email}</TableCell>
                  <TableCell>
                    <Select size="small" variant="standard" value={a.status} disabled={busy}
                      onChange={(e) => changeStatus(a.id, e.target.value)}
                      renderValue={(v) => <Chip label={v.replace('_', ' ')} size="small" color={STATUS_COLORS[v] || 'default'} />}>
                      {STATUSES.map((s) => <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>)}
                    </Select>
                  </TableCell>
                  <TableCell>{a.review_count}</TableCell>
                  <TableCell>{a.avg_score != null ? a.avg_score.toFixed(1) : '—'}</TableCell>
                  <TableCell>
                    {dayjs(a.submitted_at).format('MMM D, YYYY')}
                    {a.discarded_at && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Discarded {dayjs(a.discarded_at).format('MMM D')}
                        {a.discard_reason ? ` \u2014 ${a.discard_reason}` : ''}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Button size="small" startIcon={<Visibility />} onClick={() => open(a.id)}>View</Button>
                      {view === 'live' ? (
                        <Tooltip title="Take out of review, keep the record">
                          <span>
                            <Button size="small" color="warning" startIcon={<Block />} disabled={busy}
                              onClick={() => openDiscard([a.id])}>Discard</Button>
                          </span>
                        </Tooltip>
                      ) : (
                        <Button size="small" startIcon={<RestoreFromTrash />} disabled={busy}
                          onClick={() => restore(a.id)}>Restore</Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {sortedApps.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {view === 'discarded' ? 'Nothing has been discarded' : 'No applications yet'}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={total} page={page} onPageChange={(_, p) => setPage(p)}
          rowsPerPage={perPage} onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[20, 50, 100]} />
      </Card>
      <Dialog open={!!discardTargets} onClose={() => setDiscardTargets(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {discardTargets && discardTargets.length === 1
            ? 'Discard this application?'
            : `Discard ${discardTargets ? discardTargets.length : 0} applications?`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            It comes out of the review pool and stops appearing for reviewers. The application,
            its files and any reviews stay in the database, and you can restore it at any time.
          </DialogContentText>
          <DialogContentText variant="body2" sx={{ mb: 2 }}>
            An application that already has a submitted review cannot be discarded. Remove the
            reviewer&rsquo;s assignment first if you need to.
          </DialogContentText>
          <TextField autoFocus fullWidth multiline rows={2} label="Reason (optional)"
            placeholder="Duplicate submission, ineligible, test entry…"
            value={discardReason} onChange={(e) => setDiscardReason(e.target.value)} />
          {discardError && <Alert severity="error" sx={{ mt: 2 }}>{discardError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardTargets(null)}>Cancel</Button>
          <Button variant="contained" color="warning" disabled={busy} onClick={confirmDiscard}>Discard</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} />
    </Box>
  );
}
